import { defineStore } from 'pinia';
import * as api from 'dashboard/api/ardhisasaTickets';

const TOKEN_KEY = 'ardhisasa_token';
const JWT_KEY = 'ardhisasa_jwt';
const REFRESH_TOKEN_KEY = 'ardhisasa_refresh_token';
const TOKEN_EXPIRY_KEY = 'ardhisasa_token_expiry';

const REFRESH_BEFORE_EXPIRY_MS = 7 * 60 * 1000; // 7 minutes
const LOOKUPS_TTL_MS = 30 * 60 * 1000; // 30 minutes
const LOOKUPS_STORAGE_KEY = 'ardhisasa_lookups';
const LOOKUPS_FETCHED_KEY = 'ardhisasa_lookups_fetched';

function loadPersistedLookups() {
  try {
    return JSON.parse(localStorage.getItem(LOOKUPS_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

// Ardhisasa APIs wrap lists in various keys — try each in order
const extractList = d => {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.details)) return d.details;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.results)) return d.results;
  return [];
};

let refreshTimer = null;

export const useArdhisasaTicketsStore = defineStore('ardhisasaTickets', {
  state: () => {
    const saved = loadPersistedLookups();
    return {
      token: localStorage.getItem(TOKEN_KEY) || null,
      jwt: localStorage.getItem(JWT_KEY) || null,
      refreshTokenValue: localStorage.getItem(REFRESH_TOKEN_KEY) || null,
      tokenExpiry: localStorage.getItem(TOKEN_EXPIRY_KEY) || null,
      ticketsByTab: {
        MY: [],
        UNASSIGNED: [],
        ONGOING: [],
        CLOSED: [],
      },
      ticketsMetaByTab: {
        MY: { count: 0, next: null, previous: null },
        UNASSIGNED: { count: 0, next: null, previous: null },
        ONGOING: { count: 0, next: null, previous: null },
        CLOSED: { count: 0, next: null, previous: null },
      },
      selectedTicket: null,
      uiFlags: {
        fetchingTickets: false,
        fetchingDetail: false,
        creatingTicket: false,
        loggingIn: false,
        verifyingOtp: false,
      },
      lookups: {
        customerTypes: saved?.customerTypes || [],
        identificationTypes: saved?.identificationTypes || [],
        entities: saved?.entities || [],
        departments: [],
        sections: [],
        channelSources: saved?.channelSources || [],
      },
      lookupsLastFetched:
        Number(localStorage.getItem(LOOKUPS_FETCHED_KEY)) || null,
      ticketDraft: null,
    };
  },

  getters: {
    isAuthenticated: state => {
      if (!state.token) return false;
      if (!state.tokenExpiry) return true;
      return Date.now() < Number(state.tokenExpiry);
    },
  },

  actions: {
    setToken(token, jwt, refreshTkn, expiresIn = 3600) {
      this.token = token;
      this.jwt = jwt;
      this.refreshTokenValue = refreshTkn;
      const expiry = Date.now() + expiresIn * 1000;
      this.tokenExpiry = expiry;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(JWT_KEY, jwt);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshTkn);
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiry));
      this.scheduleRefresh(expiresIn);
    },

    clearToken() {
      this.token = null;
      this.jwt = null;
      this.refreshTokenValue = null;
      this.tokenExpiry = null;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(JWT_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
    },

    scheduleRefresh(expiresIn) {
      if (refreshTimer) clearTimeout(refreshTimer);
      const delay = expiresIn * 1000 - REFRESH_BEFORE_EXPIRY_MS;
      if (delay <= 0) {
        this.doRefresh();
        return;
      }
      refreshTimer = setTimeout(() => this.doRefresh(), delay);
    },

    initRefreshTimer() {
      if (!this.token || !this.tokenExpiry || !this.refreshTokenValue) return;
      if (refreshTimer) return; // already scheduled
      const remaining = Number(this.tokenExpiry) - Date.now();
      if (remaining <= 0) {
        this.clearToken();
        return;
      }
      const delay = remaining - REFRESH_BEFORE_EXPIRY_MS;
      if (delay <= 0) {
        this.doRefresh();
        return;
      }
      refreshTimer = setTimeout(() => this.doRefresh(), delay);
    },

    async doRefresh() {
      if (!this.refreshTokenValue) return;
      try {
        const { data } = await api.refreshToken(
          this.token,
          this.jwt,
          this.refreshTokenValue
        );
        if (!data?.details?.access_token) return;
        const { access_token, jwt, refresh_token, expires_in } = data.details;
        this.setToken(access_token, jwt, refresh_token, expires_in);
      } catch {
        // Refresh failed — leave the existing token in place so the countdown
        // reaches zero naturally and shows "Click to Login" rather than
        // immediately wiping the session.
      }
    },

    async login(username, password) {
      this.uiFlags.loggingIn = true;
      try {
        const { data } = await api.login(username, password);
        return data;
      } finally {
        this.uiFlags.loggingIn = false;
      }
    },

    async verifyOtp(username, password, otpcode) {
      this.uiFlags.verifyingOtp = true;
      try {
        const { data } = await api.verifyOtp(username, password, otpcode);
        const { access_token, jwt, refresh_token, expires_in } = data.details;
        this.setToken(access_token, jwt, refresh_token, expires_in);
        this.prefetchAll();

        // Auto-provision Chatwoot account if the user doesn't exist yet.
        // Ardhisasa's own on-file email (merged server-side in otp_verify) always takes
        // priority — the username typed into the login form (e.g. an employee number) is
        // not necessarily their email, and guessing one from it creates a fresh,
        // disconnected Chatwoot account instead of resolving to the person's real one.
        try {
          const {
            firstname,
            lastname,
            profile_photo: profilePhoto,
            ardhisasa_username: ardhisasaUsername,
            email: ardhisasaEmail,
          } = data.details;
          const email =
            ardhisasaEmail ||
            (username.includes('@')
              ? username
              : `${username}@ardhisasa.lands.go.ke`);
          const provision = await api.provisionChatwoot(
            access_token,
            jwt,
            email,
            data.details?.name || '',
            { firstname, lastname, profilePhoto, ardhisasaUsername }
          );
          if (provision.data?.sso_link) {
            return { ...data, sso_link: provision.data.sso_link };
          }
        } catch {
          // Provision failure is non-fatal — tickets module still works
        }

        return data;
      } finally {
        this.uiFlags.verifyingOtp = false;
      }
    },

    async fetchTickets(
      filterType,
      page = 1,
      search = '',
      silent = false,
      sortOrder = 'desc'
    ) {
      if (!silent) this.uiFlags.fetchingTickets = true;
      try {
        const { data } = await api.listTickets(
          this.token,
          this.jwt,
          filterType,
          page,
          search,
          sortOrder
        );
        this.ticketsByTab[filterType] = data.results || [];
        this.ticketsMetaByTab[filterType] = {
          count: data.count || 0,
          next: data.next,
          previous: data.previous,
        };
      } finally {
        if (!silent) this.uiFlags.fetchingTickets = false;
      }
    },

    prefetchAll() {
      this.fetchLookups();
      Promise.all([
        this.fetchTickets('MY', 1, '', true),
        this.fetchTickets('UNASSIGNED', 1, '', true),
        this.fetchTickets('ONGOING', 1, '', true),
        this.fetchTickets('CLOSED', 1, '', true),
      ]);
    },

    async fetchTicketDetail(requestId) {
      this.uiFlags.fetchingDetail = true;
      try {
        const { data } = await api.getTicketDetail(
          this.token,
          this.jwt,
          requestId
        );
        this.selectedTicket = data;
      } finally {
        this.uiFlags.fetchingDetail = false;
      }
    },

    async createTicket(payload, isAnonymous = false) {
      this.uiFlags.creatingTicket = true;
      try {
        const { data } = isAnonymous
          ? await api.createAnonymousTicket(this.token, this.jwt, payload)
          : await api.createTicket(this.token, this.jwt, payload);
        return data;
      } finally {
        this.uiFlags.creatingTicket = false;
      }
    },

    setTicketDraft(draft) {
      this.ticketDraft = { ...draft };
    },

    clearTicketDraft() {
      this.ticketDraft = null;
    },

    async fetchLookups(force = false) {
      if (
        !force &&
        this.lookupsLastFetched &&
        Date.now() - this.lookupsLastFetched < LOOKUPS_TTL_MS
      )
        return;
      const [ct, it, entities, sources] = await Promise.all([
        api.getCustomerTypes(this.token, this.jwt),
        api.getIdentificationTypes(this.token, this.jwt),
        api.getEntities(this.token, this.jwt),
        api.getChannelSources(this.token, this.jwt),
      ]);
      this.lookups.customerTypes = extractList(ct.data);
      this.lookups.identificationTypes = extractList(it.data);
      this.lookups.entities = extractList(entities.data);
      this.lookups.channelSources = extractList(sources.data);
      this.lookupsLastFetched = Date.now();
      localStorage.setItem(
        LOOKUPS_STORAGE_KEY,
        JSON.stringify({
          customerTypes: this.lookups.customerTypes,
          identificationTypes: this.lookups.identificationTypes,
          entities: this.lookups.entities,
          channelSources: this.lookups.channelSources,
        })
      );
      localStorage.setItem(
        LOOKUPS_FETCHED_KEY,
        String(this.lookupsLastFetched)
      );
    },

    async fetchDepartments(ministryId) {
      const { data } = await api.getDepartments(
        this.token,
        this.jwt,
        ministryId
      );
      this.lookups.departments = extractList(data);
      this.lookups.sections = [];
    },

    async fetchSections(departmentId) {
      const { data } = await api.getSections(
        this.token,
        this.jwt,
        departmentId
      );
      this.lookups.sections = extractList(data);
    },
  },
});
